import { useState, useEffect, useCallback } from 'react';
import automationApi from '../services/automationApi';
import { toast } from 'react-toastify';
import { PAGINATION_DEFAULTS } from '../constants';

const useAutomationRules = ({ defaultTriggerType } = {}) => {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    triggerType: defaultTriggerType || '',
    enabled: '',
    page: PAGINATION_DEFAULTS.PAGE,
    limit: PAGINATION_DEFAULTS.LIMIT,
  });
  const [totalItems, setTotalItems] = useState(0);

  const fetchRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.search) params.searchTerm = filters.search;
      if (filters.triggerType) params.triggerType = filters.triggerType;
      if (filters.enabled !== '') params.enabled = filters.enabled;
      params.page = filters.page;
      params.perPage = filters.limit;

      const result = await automationApi.getRules(params);
      const count = result.count || 0;
      setRules(result.data || []);
      setTotalItems(count);
      if (count > 0 && filters.page > Math.ceil(count / filters.limit)) {
        setFilters(prev => ({ ...prev, page: 1 }));
      }
    } catch (err) {
      setRules([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const updateFilters = useCallback((updates) => {
    setFilters(prev => ({ ...prev, page: 1, ...updates }));
  }, []);

  const goToPage = useCallback((page) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const toggleRule = useCallback(async (id) => {
    try {
      await automationApi.toggleRule(id);
      setRules(prev => prev.map(r =>
        r._id === id ? { ...r, enabled: !r.enabled } : r
      ));
      toast.success('Rule toggled');
    } catch (err) {
      toast.error('Failed to toggle rule');
    }
  }, []);

  const deleteRule = useCallback(async (id) => {
    try {
      await automationApi.deleteRule(id);
      setRules(prev => prev.filter(r => r._id !== id));
      setTotalItems(prev => prev - 1);
      toast.success('Rule deleted');
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  }, []);

  const executeRule = useCallback(async (id) => {
    try {
      await automationApi.executeRule(id);
      toast.success('Rule execution triggered');
    } catch (err) {
      toast.error('Failed to execute rule');
    }
  }, []);

  const duplicateRule = useCallback(async (id) => {
    try {
      const rule = await automationApi.getRule(id);
      const { _id, workflowId, version, isArchived, createdBy, updatedBy, createdAt, updatedAt, statistics, ...ruleData } = rule;
      await automationApi.createRule({ ...ruleData, name: `${ruleData.name} (Copy)` });
      toast.success('Rule duplicated');
      fetchRules();
    } catch (err) {
      toast.error('Failed to duplicate rule');
    }
  }, [fetchRules]);

  const refresh = useCallback(() => {
    fetchRules();
  }, [fetchRules]);

  const totalPages = Math.ceil(totalItems / filters.limit);

  return {
    rules,
    isLoading,
    filters,
    totalItems,
    totalPages,
    currentPage: filters.page,
    updateFilters,
    goToPage,
    toggleRule,
    deleteRule,
    executeRule,
    duplicateRule,
    refresh,
  };
};

export default useAutomationRules;
